import React, { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag } from '@rtkwlf/pro-solid-svg-icons/faFlag';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';

import { Button } from '@packages/fenrir/components/button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@packages/fenrir/components/dialog';
import { Fab } from '@packages/fenrir/components/fab';
import { FormControl } from '@packages/fenrir/components/form-control';
import { FormGroup } from '@packages/fenrir/components/form-group';
import { FormLabel } from '@packages/fenrir/components/form-label';
import { InputAdornment } from '@packages/fenrir/components/input-adornment';
import { OutlinedInput } from '@packages/fenrir/components/outlined-input';
import { Typography } from '@packages/fenrir/components/typography';

import { KEYS } from '../feature-flags';

import { CopyToken } from './copy-token/copy-token';
import { FeatureFlagControl } from './feature-flag-control';

export const FeatureFlagsDialog = () => {
  const [open, setOpen] = React.useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredFlags = useMemo(() => {
    if (!searchText.trim().length) return KEYS;

    return KEYS.filter((flag) =>
      flag.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [searchText]);

  const handleDialogOpen = () => {
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Fab
        aria-label="View feature flags"
        color="info"
        onClick={handleDialogOpen}
        size="medium"
        sx={(theme) => ({
          position: 'fixed',
          bottom: theme.spacing(9),
          left: 12,
          zIndex: theme.zIndex.drawer + 1,
        })}
      >
        <FontAwesomeIcon icon={faFlag} size="lg" />
      </Fab>

      <Dialog
        aria-labelledby="feature-flag-dialog-title"
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleDialogClose}
        // Temp Fix: https://github.com/mui/material-ui/issues/43106#issuecomment-2314809028
        closeAfterTransition={false}
      >
        <DialogTitle id="feature-flag-dialog-title">
          Supported Flags
        </DialogTitle>
        <DialogContent>
          <OutlinedInput
            aria-label="Search flags"
            autoFocus
            value={searchText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchText(event.target.value);
            }}
            fullWidth
            endAdornment={
              <InputAdornment position="end">
                <FontAwesomeIcon icon={faSearch} />
              </InputAdornment>
            }
          />

          <FormControl
            component="fieldset"
            fullWidth
            variant="standard"
            sx={{
              minHeight: 200,
              paddingX: 2,
              marginY: 2,
            }}
          >
            <FormLabel component="legend">Enable / disable flags</FormLabel>
            {filteredFlags.length >= 1 && (
              <FormGroup>
                {filteredFlags.map((flag) => (
                  <FeatureFlagControl key={flag} flag={flag} />
                ))}
              </FormGroup>
            )}
            {searchText && filteredFlags.length < 1 && (
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                No flag by that name
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <CopyToken marginLeft={2} />
          <Button variant="text" onClick={handleDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
